// ManageProfile.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import styles from "./ManageProfile.module.css";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CancelProrationModal from "./CancelProrationModal";

const BASIC_PLAN_ID = 7;

const ManageProfile = ({ accessToken, userRoles: propUserRoles }) => {
  const [userRoles, setUserRoles] = useState(propUserRoles || {});
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [cancellationInvoice, setCancellationInvoice] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const stopSubscription = () => setShowCancelModal(true);

  const navigate = useNavigate();

  const allBenefits = {
    basic: [
      "Browse all property listings",
      "Save favorite properties",
      "Contact property owners",
      "Basic search filters",
      "List your Property",
      "Edit your Property Listing",
      "Delete your Property Listing",
      "View your Property Analytics",
      "View your Property Reports",
      "View your Property Reviews",
      "View your Property Messages",
    ],
    professional: [
      "Unlimited property listings",
      "Professional Tag placement in search results",
      "Advanced analytics dashboard",
      "Client management tools",
      "Marketing automation features",
      "Social media management",
      "Blog creation and management",
      "Agency management tools",
      "Priority support",
      "Custom branding options and professional profile",
    ],
  };

  const planPricing = {
    "Basic and Free": { monthly: 0, yearly: 0 },
    "Firstland Plus": { monthly: 4.99, yearly: 39.99 },
    "Firstland Team": { monthly: 29.99, yearly: 299.99 },
  };

  const getPlanPrice = (planName, renewalType) => {
    if (!planName || planName === "Basic and Free") return 0;
    const plan = planPricing[planName];
    return plan ? plan[renewalType] || 0 : 0;
  };

  useEffect(() => {
    let isMounted = true;
    const initializeComponent = async () => {
      try {
        if (!propUserRoles) {
          const roles = await fetchUserRoles(accessToken);
          if (isMounted) setUserRoles(roles);
        }
        await fetchSubscriptionDetails(accessToken, isMounted);
      } catch (error) {
        console.error("Error initializing component:", error);
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };

    if (accessToken) {
      initializeComponent();
    }

    return () => {
      isMounted = false;
    };
  }, [accessToken, propUserRoles]);

  const fetchSubscriptionDetails = async (token, isMounted = true) => {
    try {
      const authToken = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/professional/active-subscription`,
        {
          headers: { Authorization: authToken },
        }
      );

      const subscription = response.data?.subscription;

      if (subscription && isMounted) {
        const planPrice =
          subscription.amount_paid ||
          getPlanPrice(subscription.plan_name, subscription.renewal_type);
        setSubscriptionDetails({
          planId: subscription.plan_id,
          planName: subscription.plan_name,
          renewalType: subscription.renewal_type,
          status: subscription.status,
          trialEndDate: subscription.trial_end_date,
          expirationDate: subscription.expiration_date,
          amountPaid: planPrice,
          stripeSubscriptionId: subscription.stripe_subscription_id,
          features: subscription.features?.split(", ") || [],
        });
      } else if (isMounted) {
        setSubscriptionDetails({
          planId: BASIC_PLAN_ID,
          planName: "Basic and Free",
          renewalType: "monthly",
          status: "active",
          amountPaid: 0,
          features: [],
        });
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
      if (isMounted) {
        setSubscriptionDetails({
          planId: BASIC_PLAN_ID,
          planName: "Basic and Free",
          renewalType: "monthly",
          status: "active",
          amountPaid: 0,
          features: [],
        });
      }
    }
  };

  const fetchUserRoles = async (token) => {
    try {
      const authToken = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/dash/roles`,
        {
          headers: { Authorization: authToken },
        }
      );

      if (response.status === 200 && response.data.success) {
        const userRoles = {
          isCustomer: true,
          isLandlord: response.data.isLandlord || false,
          isProfessional: response.data.isProfessional || false,
          isBroker: response.data.isBroker || false,
          isRealtor: response.data.isRealtor || false,
          hasActiveSubscription: false,
        };

        if (userRoles.isProfessional) {
          try {
            const subscriptionResponse = await axios.get(
              `${
                import.meta.env.VITE_BACKEND_URL
              }/professional/active-subscription`,
              {
                headers: { Authorization: authToken },
              }
            );
            if (subscriptionResponse.data?.subscription) {
              const subscription = subscriptionResponse.data.subscription;
              userRoles.hasActiveSubscription =
                subscription.status === "active" &&
                subscription.plan_id !== BASIC_PLAN_ID;
            }
          } catch (error) {
            console.error("Error checking subscription status:", error);
            userRoles.hasActiveSubscription = false;
          }
        }

        return userRoles;
      } else {
        console.error(
          "Error fetching user roles:",
          response.data.message || "Unknown error"
        );
        return null;
      }
    } catch (error) {
      console.error("Error fetching user roles:", error);
      return null;
    }
  };

  // const stopSubscription = async () => {
  //   try {
  //     const authToken = accessToken.startsWith("Bearer ")
  //       ? accessToken
  //       : `Bearer ${accessToken}`;
  //     const response = await axios.post(
  //       `${
  //         import.meta.env.VITE_BACKEND_URL
  //       }/payment/check-cancel-subscription-prorate`,
  //       {},
  //       {
  //         headers: { Authorization: authToken },
  //       }
  //     );

  //     if (response.data?.success && response.data.invoice) {
  //       setCancellationInvoice(response.data.invoice);
  //       setShowCancelModal(true);
  //     } else {
  //       toast.error("Failed to fetch prorate charges.");
  //     }
  //   } catch (error) {
  //     console.error("Error stopping subscription", error);
  //     toast.error(
  //       "There was an error stopping your subscription. Please try again."
  //     );
  //   }
  // };

  const handleCancelProceed = async () => {
    setShowCancelModal(false);
    try {
      const authToken = accessToken.startsWith("Bearer ")
        ? accessToken
        : `Bearer ${accessToken}`;
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/payment/billing-portal-session`,
        {},
        {
          headers: { Authorization: authToken },
        }
      );

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Failed to redirect to Stripe cancellation portal.");
      }
    } catch (error) {
      console.error("Error redirecting to Stripe billing portal:", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (initialLoading) {
    return (
      <div className={styles.loadingMessage}>
        Loading subscription details...
      </div>
    );
  }

  return (
    <>
      <div className={styles.profileContainer}>
        <h1>Manage Subscription</h1>

        {subscriptionDetails ? (
          <div className={styles.subscriptionDetails}>
            <div className={styles.planSummary}>
              <div className={styles.planHeader}>
                <h3>{subscriptionDetails.planName || "Basic and Free"}</h3>
                <span
                  className={`${styles.planStatus} ${
                    subscriptionDetails.status || "active"
                  }`}
                >
                  {subscriptionDetails.status || "active"}
                </span>
              </div>
              <div className={styles.planPrice}>
                <span>
                  $
                  {subscriptionDetails.planId === 7
                    ? 0
                    : subscriptionDetails.amountPaid > 0
                    ? subscriptionDetails.amountPaid
                    : getPlanPrice(
                        subscriptionDetails.planName,
                        subscriptionDetails.renewalType
                      )}
                </span>
                <span>
                  /
                  {subscriptionDetails.renewalType === "monthly"
                    ? "month"
                    : "year"}
                </span>
              </div>
            </div>

            {subscriptionDetails.expirationDate && (
              <div className={styles.subscriptionDates}>
                <div className={styles.dateItem}>
                  <span className={styles.dateLabel}>Next Payment</span>
                  <span className={styles.dateValue}>
                    {new Date(
                      subscriptionDetails.expirationDate
                    ).toLocaleDateString()}
                  </span>
                </div>
                {subscriptionDetails.trialEndDate && (
                  <div className={styles.dateItem}>
                    <span className={styles.dateLabel}>Trial Ends</span>
                    <span className={styles.dateValue}>
                      {new Date(
                        subscriptionDetails.trialEndDate
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* trial box */}

            {/* Trial Information Box - Only for paid plans */}
            {subscriptionDetails.planId !== 7 && (
              <div className={styles.trialBox}>
                <h3>Trial Information</h3>
                <div className={styles.trialDetails}>
                  <div className={styles.trialItem}>
                    <span className={styles.trialLabel}>Trial Status</span>
                    <span className={styles.trialValue}>
                      {subscriptionDetails.trialEndDate &&
                      new Date(subscriptionDetails.trialEndDate) > new Date()
                        ? "Active"
                        : "Completed"}
                    </span>
                  </div>

                  {subscriptionDetails.trialEndDate && (
                    <div className={styles.trialItem}>
                      <span className={styles.trialLabel}>Trial End Date</span>
                      <span className={styles.trialValue}>
                        {new Date(
                          subscriptionDetails.trialEndDate
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  )}

                  {subscriptionDetails.expirationDate && (
                    <div className={styles.trialItem}>
                      <span className={styles.trialLabel}>
                        Plan Renewal Date
                      </span>
                      <span className={styles.trialValue}>
                        {new Date(
                          subscriptionDetails.expirationDate
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={styles.planDetails}>
              <h3>Plan Description</h3>
              <p className={styles.planDescription}>
                {!subscriptionDetails.planId || subscriptionDetails.planId === 7
                  ? "Access to essential FirstLand features at no cost. Perfect for casual users looking to explore properties."
                  : "Premium access to FirstLand's professional tools and features to help you grow your real estate business."}
              </p>

              <h3>Your Plan Benefits</h3>
              <div className={styles.benefitsList}>
                {allBenefits.basic.map((feature, index) => (
                  <div key={index} className={styles.benefitItem}>
                    <CheckCircleIcon className={styles.checkmark} />
                    <span>{feature}</span>
                  </div>
                ))}
                {(subscriptionDetails.planId !== 7 ||
                  userRoles.hasActiveSubscription) &&
                  allBenefits.professional.map((feature, index) => (
                    <div key={index} className={styles.benefitItem}>
                      <CheckCircleIcon className={styles.checkmark} />
                      <span>{feature}</span>
                    </div>
                  ))}
              </div>

              {/* Add unavailable features section for Firstland Plus plan */}
              {subscriptionDetails?.planName === "Firstland Plus" && (
                <>
                  <h3>Team Plan Features (Not Available)</h3>
                  <div className={styles.benefitsList}>
                    <div
                      className={`${styles.benefitItem} ${styles.unavailable}`}
                    >
                      <CancelIcon className={styles.crossmark} />
                      <span>Dedicated Account Manager</span>
                    </div>
                    <div
                      className={`${styles.benefitItem} ${styles.unavailable}`}
                    >
                      <CancelIcon className={styles.crossmark} />
                      <span>Multi-User Lead Management</span>
                    </div>
                    <div
                      className={`${styles.benefitItem} ${styles.unavailable}`}
                    >
                      <CancelIcon className={styles.crossmark} />
                      <span>Up to 10 User Accounts</span>
                    </div>
                    <div
                      className={`${styles.benefitItem} ${styles.unavailable}`}
                    >
                      <CancelIcon className={styles.crossmark} />
                      <span>Add/Remove Employees</span>
                    </div>
                    <div
                      className={`${styles.benefitItem} ${styles.unavailable}`}
                    >
                      <CancelIcon className={styles.crossmark} />
                      <span>Publish Agency Structure</span>
                    </div>
                  </div>
                </>
              )}

              {subscriptionDetails.planId === 7 &&
                !userRoles.hasActiveSubscription && (
                  <>
                    <h3>Professional Benefits (Not Available)</h3>
                    <div className={styles.benefitsList}>
                      {allBenefits.professional.map((feature, index) => (
                        <div
                          key={index}
                          className={`${styles.benefitItem} ${styles.unavailable}`}
                        >
                          <CancelIcon className={styles.crossmark} />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
            </div>

            <div className={styles.managementActions}>
              {subscriptionDetails.status !== "canceled" && (
                <>
                  <button
                    className={`${styles.actionButton} ${styles.updateButton}`}
                    onClick={() => navigate("/subscription-plans")}
                  >
                    Change Plan
                  </button>
                  {subscriptionDetails.planId !== 7 && (
                    <button
                      className={`${styles.actionButton} ${styles.cancelButton}`}
                      onClick={stopSubscription}
                    >
                      Cancel Subscription
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.loadingMessage}>
            Loading subscription details...
          </div>
        )}
      </div>
      <div className={styles.profileContainer}>
        <>
          <h1>Manage Profile</h1>
          <p className={styles.warningMessage}>
            Warning: Deleting your account is irreversible. You will not be able
            to recover your account once it's deleted. Please proceed with
            caution.
          </p>
          {/* <button className={styles.button} onClick={deleteAccount}>
            Delete Account
          </button> */}
          <h4>Currently not available, please email Team@firstland.homes</h4>
          <h4>Expect response within 3 business days</h4>
        </>
      </div>

      {showCancelModal && (
        <CancelProrationModal
          accessToken={accessToken}
          onClose={() => setShowCancelModal(false)}
          onDone={() => fetchSubscriptionDetails(accessToken, true)} // optional refresh after they return
        />
      )}
    </>
  );
};

export default ManageProfile;
